import { addTo } from '../../../util/index.js';
import { enteredAmount } from '../../case-vouchers/index.js';
import { getCostCeilingSummary, isDynamicCeilingVoucher, } from '../interest/cost-ceiling.js';
import { isOpen, openBefore, amountOn, dayAmounts, firstUnpaidDay, ledgerOpen, paidOnDay, trancheOpen, } from './ledger.js';
// What the ceiling is set from. Debt, gross: expiry does not shrink the
// ceiling, whether or not the vouchers arrive written down. A correction
// does, as the corrected part never existed (D11). Credits only pay, as
// payments do. `corrected`: per voucher, what corrections took from it.
export const ceilingBasis = (inputVouchers, corrected) => inputVouchers.flatMap((voucher) => {
    const gross = enteredAmount(voucher);
    if (gross >= 0) {
        return [];
    }
    const correction = Math.min(corrected.get(voucher.caseVoucherId) ?? 0, -gross);
    return [{ ...voucher, amount: gross + correction }];
});
export const createCostCeiling = (inputVouchers, corrected, costCeiling) => {
    const summary = getCostCeilingSummary(ceilingBasis(inputVouchers, corrected), costCeiling);
    if (summary === null) {
        return null;
    }
    return {
        budget: summary.dynamicBudget,
        covers: (voucher) => isDynamicCeilingVoucher(voucher, costCeiling.voucherTypesCeiling),
    };
};
const openSegments = (ledger, index) => ledger.tranches.filter(isOpen).map((tranche) => ({
    ledger: index,
    tranche,
    from: firstUnpaidDay(tranche),
}));
const openOn = ({ tranche }, day) => openBefore(tranche, day + 1) - openBefore(tranche, day);
// A ledger's covered cost that was paid. A correction is not a payment: the
// corrected cost never existed.
const paidCost = (ledger) => ledger.paid - ledger.corrected;
// The claimable covered cost: the oldest open cost, up to the budget less
// the cost paid. On the day the budget runs out, everything open that day
// shares what is left pro rata (see `claimsAt`), in whole øre by largest
// remainder, a tie going to the ledger first in `ledgers` (allocation order),
// then to the lower stream. Null when everything open is claimable.
export const claimable = (ledgers, budget) => {
    let room = budget;
    let open = 0;
    for (const ledger of ledgers) {
        room -= paidCost(ledger);
        open += ledgerOpen(ledger);
    }
    if (open <= room) {
        return null;
    }
    room = Math.max(0, room);
    const segments = ledgers
        .flatMap(openSegments)
        .toSorted((a, b) => a.from - b.from);
    const points = [
        ...new Set(segments.flatMap((segment) => [segment.from, segment.tranche.to])),
    ].toSorted((a, b) => a - b);
    let active = [];
    let next = 0;
    for (let i = 0; i < points.length - 1; i++) {
        const from = points[i];
        active = active.filter((segment) => segment.tranche.to > from);
        while (next < segments.length && segments[next].from === from) {
            active.push(segments[next++]);
        }
        const openTo = (day) => active.reduce((sum, segment) => sum +
            openBefore(segment.tranche, day) -
            openBefore(segment.tranche, from), 0);
        const total = openTo(points[i + 1]);
        if (total <= room) {
            room -= total;
            continue;
        }
        // The room covers the days before `boundary` in full.
        let boundary = from;
        let high = points[i + 1];
        while (high - boundary > 1) {
            const middle = (boundary + high) >> 1;
            if (openTo(middle) <= room) {
                boundary = middle;
            }
            else {
                high = middle;
            }
        }
        return claimsAt(ledgers, active.filter((segment) => openOn(segment, boundary) > 0), boundary, room - openTo(boundary));
    }
    throw new Error('Open covered cost exceeds the room but never fills it');
};
const claimsAt = (ledgers, active, boundary, room) => {
    // Each part of the day: what of it is open, and what of it was paid.
    const parts = new Map();
    for (const segment of active) {
        const { stream } = segment.tranche;
        const key = `${segment.ledger}|${stream}`;
        const open = openOn(segment, boundary);
        const part = parts.get(key);
        if (part === undefined) {
            parts.set(key, { ledger: segment.ledger, stream, open, paid: 0 });
        }
        else {
            part.open += open;
        }
    }
    for (const part of parts.values()) {
        part.paid = dayAmounts(ledgers[part.ledger], boundary, part.stream).paid;
    }
    // The parts share what is left and what of the day they paid, pro rata to
    // what of the day they have open or paid, so paying a share changes no
    // other. A part that paid more than its share gets nothing.
    const ordered = [...parts.values()].toSorted((a, b) => a.paid * (b.open + b.paid) - b.paid * (a.open + a.paid));
    const sharing = [];
    let pool = room;
    let counted = 0;
    for (const part of ordered) {
        if (counted > 0 && part.paid * counted >= pool * (part.open + part.paid)) {
            break;
        }
        sharing.push(part);
        pool += part.paid;
        counted += part.open + part.paid;
    }
    const shares = sharing.map((part) => {
        const exact = pool * (part.open + part.paid) - part.paid * counted;
        return {
            part,
            exact: exact / counted,
            share: Math.floor(exact / counted),
            remainder: exact % counted,
        };
    });
    let left = room - shares.reduce((sum, { share }) => sum + share, 0);
    for (const entry of shares.toSorted((a, b) => b.remainder - a.remainder ||
        a.part.ledger - b.part.ledger ||
        a.part.stream - b.part.stream)) {
        if (left === 0) {
            break;
        }
        entry.share++;
        left--;
    }
    const claims = new Map(ledgers.map((ledger) => [
        ledger,
        { boundary, shares: new Map(), exactShares: new Map() },
    ]));
    for (const { part, exact, share } of shares) {
        const claim = claims.get(ledgers[part.ledger]);
        claim.shares.set(part.stream, share);
        claim.exactShares.set(part.stream, exact);
    }
    return claims;
};
// For each of `days`, what is left of the budget once the open cost of the
// days before is claimed, and the open cost of the day itself.
export const roomByDay = (ledgers, budget, days) => {
    const paid = ledgers.reduce((sum, ledger) => sum + paidCost(ledger), 0);
    const segments = ledgers.flatMap(openSegments);
    return new Map(days.map((day) => {
        let before = 0;
        let open = 0;
        for (const segment of segments) {
            before += openBefore(segment.tranche, day);
            open += openOn(segment, day);
        }
        return [day, { room: Math.max(0, budget - paid - before), open }];
    }));
};
// What of `tranche` can be paid under `claim`. Each call consumes the
// boundary day's share it returns, as `consume` visits a tranche once and
// stops once anything it returns is not paid in full.
export const payableUnder = (claim) => {
    const left = new Map(claim.shares);
    return (tranche) => {
        if (tranche.to <= claim.boundary) {
            return trancheOpen(tranche);
        }
        if (tranche.from > claim.boundary) {
            return 0;
        }
        const before = openBefore(tranche, claim.boundary);
        const share = Math.min(amountOn(tranche, claim.boundary) - paidOnDay(tranche, claim.boundary), left.get(tranche.stream) ?? 0);
        addTo(left, tranche.stream, -share);
        return before + share;
    };
};
// The open cost of `ledger` on days after the boundary, and on it, per
// stream, of the streams in `streams`.
export const openBeyond = (ledger, claim, streams) => {
    let after = 0;
    const onBoundary = new Map();
    for (const segment of openSegments(ledger, 0)) {
        const { stream, to } = segment.tranche;
        if (!streams.has(stream) || to <= claim.boundary) {
            continue;
        }
        after +=
            openBefore(segment.tranche, to) -
                openBefore(segment.tranche, claim.boundary + 1);
        const open = openOn(segment, claim.boundary);
        if (open > 0) {
            addTo(onBoundary, stream, open);
        }
    }
    return { after, onBoundary };
};
//# sourceMappingURL=ceiling.js.map